import React, { useEffect, useMemo, useState } from "react";

import { FileDrop } from "../../components/FileDrop";
import { Text } from "../../components/Text";
import {
  Flag,
  LanguageCode,
  renderLanguageFromCode,
  SUICustomerLanguageDropdown,
} from "../../customers/customerlanguages";
import { ManualTranslationRequestForm } from "../manual-translation-request/ManualTranslationRequestForm";
import fromPairs from "lodash/fromPairs";
import isNull from "lodash/isNull";
import isEmpty from "lodash/isEmpty";
import { useSelector } from "react-redux";
import { useGetAuthQuery } from "../../api/authApi";
import { useGetCustomerQuery } from "../../api/customerApi";
import { requestCustomRequest } from "../../api/requestTranslationApi";
import { validateFileExtension } from "../../utils/files";
import { ALLOWED_EXTENSIONS } from "./MachineTranslateFile";
import { RootState } from "../../utils/store";
import { Dimmer, Icon } from "semantic-ui-react";
import { Spinner } from "../../utils/Spinner";
import { Button } from "../../components/tailwind";
import { NOOP } from "../..";

export const CustomRequestFile: React.FC = () => {
  const { data: customer, isLoading } = useGetCustomerQuery();
  const { data: auth, isLoading: isAuthLoading } = useGetAuthQuery();
  const token = useSelector((state: RootState) => state.auth.token);
  const [isRequesting, setIsRequesting] = useState(false);
  const [jobIds, setJobIds] = useState<{ [lang: string]: string }>({});
  const [email, setEmail] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [selectedFile, setSelectedFile] = useState<File>(null);
  const isSelectedFileInvalid = useMemo((): boolean => {
    if (isNull(selectedFile)) return false;
    return !validateFileExtension(selectedFile, ALLOWED_EXTENSIONS);
  }, [selectedFile]);

  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState<
    LanguageCode
  >();
  const [selectedLanguages, setSelectedLanguages] = useState<LanguageCode[]>(
    []
  );

  const allLanguagesHasBeenSelected =
    selectedLanguages.length === customer?.languages.length - 1;

  useEffect(() => {
    if (selectedLanguages.includes(selectedSourceLanguage)) {
      setSelectedLanguages(
        [...selectedLanguages].filter((code) => code !== selectedSourceLanguage)
      );
    }
  }, [selectedSourceLanguage]);

  useEffect(() => {
    if (customer && customer.config.tag_input_language) {
      setSelectedSourceLanguage(customer.config.tag_input_language);
    }
  }, [customer]);

  const handleSelectLanguage = (code: LanguageCode): void => {
    if (code === selectedSourceLanguage) {
      return;
    }
    if (selectedLanguages.includes(code)) {
      setSelectedLanguages((prev) => [...prev].filter((c) => c !== code));
      return;
    }
    setSelectedLanguages((prev) => [...prev, code]);
  };

  const handleSelectAll = (): void => {
    if (allLanguagesHasBeenSelected) {
      setSelectedLanguages([]);
      return;
    }
    const languageCodesToSelect = customer.languages
      .filter(({ code }) => code !== selectedSourceLanguage)
      .map(({ code }) => code);
    setSelectedLanguages(languageCodesToSelect);
  };

  const handleRequestCustomRequest = async (): Promise<void> => {
    if (!canSendRequest) return;
    setIsRequesting(true);
    try {
      const jobId = await requestCustomRequest(
        token,
        email,
        selectedSourceLanguage,
        selectedLanguages,
        isUrgent,
        additionalInstructions,
        selectedFile,
        null
      );
      setJobIds(
        fromPairs(selectedLanguages.map((language) => [language, jobId]))
      );
    } finally {
      setIsRequesting(false);
    }
  };

  const resetAllStates = (): void => {
    setJobIds({});
    setIsUrgent(false);
    setAdditionalInstructions("");
    setSelectedFile(null);
    setSelectedSourceLanguage(null);
    setSelectedLanguages([]);
    setIsRequesting(false);
  };

  useEffect(() => {
    if (isAuthLoading || !auth) return;
    setEmail(auth.user.email);
  }, [auth, isAuthLoading]);

  const canSendRequest = useMemo((): boolean => {
    return (
      selectedSourceLanguage &&
      email &&
      selectedFile &&
      !isSelectedFileInvalid &&
      !!additionalInstructions &&
      !!selectedLanguages.length
    );
  }, [
    selectedSourceLanguage,
    selectedLanguages,
    selectedFile,
    email,
    isSelectedFileInvalid,
    additionalInstructions,
    selectedLanguages,
  ]);

  return (
    <div className="tw-grid tw-grid-cols-2 tw-gap-8">
      <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-2">
        <div>
          <SUICustomerLanguageDropdown
            loading={isLoading}
            clearable
            data-testid="manual-translation-request-select-source-language-dropdown"
            fluid
            placeholder="Select source language"
            selection
            value={selectedSourceLanguage}
            onChange={(e, { value }): void =>
              setSelectedSourceLanguage(value as LanguageCode)
            }
          />
        </div>
        <div>
          <FileDrop
            selectedFile={selectedFile}
            isInvalid={isSelectedFileInvalid}
            onChange={(file): void => {
              setSelectedFile(file);
            }}
            data-testid="custom-request-file-drop"
            contentUnselected={
              <>
                <Text>
                  Supported file types: PDF, Word (.docx), Excel (.xlsx), CSV
                </Text>
                <Text color="grey">
                  Also supported: Plain text (.txt), Powerpoint (.pptx)
                </Text>
                <small className="tw-text-gray-500">
                  <b>Note:</b> All words in the file are counted automatically
                  and form the basis of the assignment and billing.
                </small>
              </>
            }
          />
          <ManualTranslationRequestForm
            email={email}
            setEmail={setEmail}
            isUrgent={isUrgent}
            setIsUrgent={setIsUrgent}
            additionalInstructions={additionalInstructions}
            setAdditionalInstructions={setAdditionalInstructions}
            additionalInstructionLabel="Explain your issue *"
          ></ManualTranslationRequestForm>
        </div>
      </div>
      <div>
        <table className="txu-table txu-compact txu-clickable-rows tw-mb-4">
          <thead>
            <tr>
              <th>Language</th>
              <th>Included in Custom Request</th>
            </tr>
          </thead>
          <tbody>
            {customer?.languages.map((language) => (
              <tr
                data-testid={`manual-translation-request-table-row-${language.code}`}
                key={language.code}
                onClick={(): void => handleSelectLanguage(language.code)}
              >
                <td>
                  <Flag content={language.flag} /> {language.short_name}{" "}
                  <span className="tw-text-gray-500">({language.code})</span>
                </td>
                <td>
                  <input
                    type="checkbox"
                    className="txu-input"
                    data-testid={`manual-translation-request-table-row-${language.code}-checkbox`}
                    checked={selectedLanguages.includes(language.code)}
                    disabled={language.code === selectedSourceLanguage}
                    onChange={NOOP}
                  />
                </td>
              </tr>
            ))}
            <tr onClick={(): void => handleSelectAll()}>
              <td>Select All</td>
              <td>
                <input
                  type="checkbox"
                  data-testid="manual-translation-request-table-row-select-all-checkbox"
                  checked={allLanguagesHasBeenSelected}
                  onChange={(): void => handleSelectAll()}
                  className="txu-input"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <Button
          data-testid="manual-translation-request-send-request-button"
          variant="primary"
          content="Send request"
          className="tw-float-end"
          onClick={(): Promise<void> => handleRequestCustomRequest()}
          disabled={!canSendRequest}
        />
      </div>

      <Dimmer active={isRequesting || !isEmpty(jobIds)} page>
        {isRequesting ? (
          <Spinner
            inverted
            content="Sending Custom Request..."
            data-testid="manual-translation-request-loading"
            size="medium"
          />
        ) : (
          <>
            <Icon name="check" />
            <Text size="large">Custom Request received!</Text>
            <Text size="small">
              You will receive an email at <b>{email}</b> when the Custom
              request is complete.
            </Text>
            <ul data-testid="manual-translation-request-job-ids">
              {Object.entries(jobIds).map(([languageCode, jobId]) => {
                return (
                  <li key={languageCode} className="tw-my-2">
                    <strong>
                      {renderLanguageFromCode(
                        languageCode,
                        customer?.languages ?? []
                      )}
                    </strong>
                    : {jobId}
                  </li>
                );
              })}
            </ul>
            <Text size="small">
              Save the job ID(s) for use in correspondence with us.
              <br />
              Any related questions can be sent to{" "}
              <a
                href="mailto:translations@textual.se"
                style={{ color: "white" }}
              >
                translations@textual.se
              </a>
            </Text>
            <hr className="tw-my-4 tw-border-gray-600" />
            <Button
              data-testid="manual-translation-request-done-button"
              content="Done"
              variant="primary"
              onClick={resetAllStates}
            />
          </>
        )}
      </Dimmer>
    </div>
  );
};
